import React, { useEffect, useState } from "react";
import axios from "axios";
import CONFIG from "../config";
import { User, Phone, ShieldCheck, Mail, MapPin, Edit3, ArrowLeft, Loader2 } from "lucide-react";
import { usePatient } from "../context/PatientContext";
import { useTheme } from "../context/ThemeContext";
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from "framer-motion";

const MyProfileInfo = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const baseURL = CONFIG.BASE_URL;
  const { doctorInfo } = usePatient();
  const { theme, isDarkMode } = useTheme();

  const userId = doctorInfo?.MANDALI_ID;
  const token = localStorage.getItem("authToken");

  useEffect(() => {
    if (!userId) {
      setError("User ID not found. Please log in.");
      setLoading(false);
      return;
    }

    axios.get(`${baseURL}/mandali/profile?userId=${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        doctorId: doctorInfo.MANDALI_ID,
        clinicId: doctorInfo.CLINIC_ID,
        roleId: doctorInfo.MANDALI_ROLE
      },
    })
      .then((res) => {
        setProfile(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError("Failed to load profile details. Please try again later.");
        setLoading(false);
      });
  }, [userId, baseURL, token, doctorInfo]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] w-full" style={{ backgroundColor: theme.primary }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <Loader2 className="w-12 h-12" style={{ color: theme.primary }} />
        </motion.div>
        <p className="mt-4 font-medium animate-pulse" style={{ color: theme.textSecondary }}>Loading your profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] p-4 text-center" style={{ backgroundColor: theme.body }}>
        <div className="p-6 rounded-2xl border max-w-md" style={{ backgroundColor: theme.card, borderColor: theme.danger + '20' }}>
          <p className="font-semibold text-lg mb-2" style={{ color: theme.danger }}>Oops! Something went wrong</p>
          <p className="mb-6" style={{ color: theme.textSecondary }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 text-white rounded-lg transition-colors shadow-sm"
            style={{ backgroundColor: theme.danger }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const getInitials = (name) => {
    if (!name) return "??";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const InfoRow = ({ icon: Icon, label, value, colorClass, iconColor }) => (
    <div className="flex items-center p-4 rounded-xl border transition-all group"
      style={{
        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        borderColor: theme.border
      }}>
      <div className={`p-3 rounded-lg mr-4 transition-transform group-hover:scale-110`}
        style={{ backgroundColor: iconColor + '15', color: iconColor }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color: theme.textSecondary }}>{label}</p>
        <p className="font-medium text-lg" style={{ color: theme.text }}>{value || "Not Provided"}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 transition-colors duration-500" style={{ background: 'transparent' }}>
      <div className="max-w-4xl mx-auto">
        {/* Header Navigation */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center transition-colors group"
            style={{ color: theme.textSecondary }}
          >
            <ArrowLeft className="w-5 h-5 mr-2 transition-transform group-hover:-translate-x-1" />
            <span className="font-medium hover:opacity-80 transition-opacity">Back</span>
          </button>
          <h1 className="text-2xl font-bold" style={{ color: theme.text }}>Account Details</h1>
          <div className="w-10"></div> {/* Spacer for symmetry */}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-[2rem] shadow-2xl overflow-hidden border"
          style={{
            backgroundColor: theme.card,
            borderColor: theme.border,
            boxShadow: isDarkMode ? '0 25px 50px -12px rgba(0,0,0,0.5)' : '0 25px 50px -12px rgba(0,0,0,0.1)'
          }}
        >
          {/* Profile Header Decoration */}
          <div className="h-32 relative overflow-hidden"
            style={{ backgroundImage: `linear-gradient(to right, ${theme.primary}, ${theme.secondary}, ${theme.brownOne})` }}>
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at center, white 0%, transparent 70%)' }}></div>
          </div>

          <div className="px-8 pb-10">
            {/* Avatar Section */}
            <div className="relative -mt-16 mb-8 flex flex-col items-center">
              <div className="p-1.5 rounded-full shadow-lg" style={{ backgroundColor: theme.card }}>
                <div className="w-32 h-32 rounded-full flex items-center justify-center ring-4 overflow-hidden font-bold text-4xl border"
                  style={{
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                    color: theme.primary,
                    ringColor: theme.secondary + '30',
                    borderColor: theme.border
                  }}>
                  {getInitials(profile?.MANDALI_FULL_NAME)}
                </div>
              </div>
              <div className="mt-4 text-center">
                <h2 className="text-3xl font-bold" style={{ color: theme.text }}>{profile?.MANDALI_FULL_NAME}</h2>
                <span className="inline-flex items-center px-3 py-1 mt-2 rounded-full text-xs font-bold uppercase tracking-wide"
                  style={{ backgroundColor: theme.primary + '15', color: theme.primary }}>
                  {profile?.MANDALI_ROLE === 1 ? "System Administrator" : "Medical Professional"}
                </span>
              </div>
            </div>

            <hr className="mb-10" style={{ borderColor: theme.border }} />

            {/* Profile Content */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              <InfoRow
                icon={User}
                label="Full Name"
                value={profile?.MANDALI_FULL_NAME}
                iconColor={theme.primary}
              />
              <InfoRow
                icon={Phone}
                label="Phone Number"
                value={profile?.MANDALI_MOB_NO}
                iconColor={theme.success}
              />
              <InfoRow
                icon={ShieldCheck}
                label="Account Role"
                value={profile?.MANDALI_ROLE === 1 ? "Admin" : "Doctor"}
                iconColor={theme.brownOne}
              />
              <InfoRow
                icon={Mail}
                label="Email Address"
                value={profile?.MANDALI_EMAIL}
                iconColor={theme.secondary}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center border-t pt-10" style={{ borderColor: theme.border }}>
              <Link
                to={`/edit-doctor/${userId}`}
                className="flex items-center justify-center w-full sm:w-auto px-8 py-3 text-white rounded-xl font-semibold transition-all shadow-lg hover:-translate-y-0.5"
                style={{
                  backgroundColor: theme.primary,
                  boxShadow: `0 10px 15px -3px ${theme.primary}40`
                }}
              >
                <Edit3 className="w-5 h-5 mr-2" />
                Update Profile Info
              </Link>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center justify-center w-full sm:w-auto px-8 py-3 bg-transparent border rounded-xl font-semibold transition-all hover:bg-black/5"
                style={{ color: theme.textSecondary, borderColor: theme.border }}
              >
                Go Back
              </button>
            </div>
          </div>
        </motion.div>

        {/* Footer Info */}
        <p className="mt-8 text-center text-sm" style={{ color: theme.textSecondary }}>
          Information managed by Amrutdhara Clinic System
        </p>
      </div>
    </div>
  );
};

export default MyProfileInfo;
